/**
 * Tells a master admin that this page and a server it talks to were built from
 * different FlatBuffer schemas, and how the page is reading what that server
 * sends: as before, without some messages, or not at all.
 *
 * Shown only to master admins, by the role being previewed, and only once the
 * page has met such a schema (`flatbuffers/schemaSkew.ts`).
 */

import React from "react";
import { Alert } from "antd";
import type { UserLoggedInterface } from "../../interfaces/User.interface";
import { schemaSkewSnapshot, type SkewObservation } from "../../flatbuffers/schemaSkew";
import useSchemaSkew from "../../flatbuffers/useSchemaSkew";
import { isMasterAdmin } from "../../utils/isMasterAdmin";

/** Outcomes for which the server vouched that the page reads its schema correctly. */
const VOUCHED = new Set(["exact", "proven", "extended"]);

/** What the page does with what it met, by outcome. */
const READING: Record<string, string> = {
  proven: "read as before: proven to read the same",
  extended: "read as before, leaving out messages that hold a value this page has no name for",
  unverified:
    "read where every value is one this page names: the server could not compare the schemas",
  unknown: "read as before: the server could not compare the schemas",
  transcoded: "not read: the server's schema changed what this page reads",
  inspect_only: "not read: the server's schema does not describe what this page reads",
  degraded: "not read: the server's schema changed what this page reads",
};

function describe(seen: SkewObservation): string {
  const where = seen.channel === "ws" ? "Monitoring" : "API";
  const origin = [seen.provenance, seen.signature && `signature ${seen.signature}`]
    .filter(Boolean)
    .join(", ");
  const reading = READING[seen.outcome] ?? seen.outcome;
  const times = seen.count === 1 ? "once" : `${seen.count} times`;
  return `${where} ${seen.typeName}, schema ${seen.schema.slice(0, 8)}${
    origin ? ` (${origin})` : ""
  }: ${reading}; met ${times}.`;
}

function SchemaSkewBanner({ userLogged }: { userLogged: UserLoggedInterface | null | undefined }) {
  const observations = useSchemaSkew(schemaSkewSnapshot);
  if (observations.length === 0 || !isMasterAdmin(userLogged)) {
    return null;
  }
  const unvouched = observations.some((seen) => !VOUCHED.has(seen.outcome));
  return (
    <Alert
      type={unvouched ? "warning" : "info"}
      showIcon
      closable
      style={{ margin: "0 0 12px" }}
      message="This page and its server were built from different FlatBuffer schemas"
      description={
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {observations.map((seen) => (
            <li key={`${seen.channel}|${seen.schema}|${seen.typeName}`}>{describe(seen)}</li>
          ))}
        </ul>
      }
    />
  );
}

export default SchemaSkewBanner;
